import * as _ from 'lodash';
import {
  Component,
  OnInit,
  EventEmitter,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { GroupModel } from '@app/core/models/groups/group.model';
import { ActivatedRoute, Router, Params } from '@angular/router';
import { MultiSelectModel } from '@app/shared/models/multi-select.model';
import { UserGroupModel } from '@app/core/models/groups/user-group-model';
import { GroupService } from '@app/core/services/group.service';
import { UserDetailModel } from '@app/core/models/users/user-detail.model';
import { UserUpdateModel } from '@app/core/models/users/user-update.model';
import { UserModel } from '@app/core/models/users/user.model';
import { UserService } from '@app/core/services/user.service';
import {
  debounceTime,
  distinctUntilChanged,
  tap,
  takeUntil,
} from 'rxjs/operators';
import { UserGroupLineModel } from '@app/core/models/users/user-group-line.model';
import { AuthenticationInfoService } from '@app/core/guards/authentication-info.service';
import { IDropdownSettings } from 'ng-multiselect-dropdown';
import { AdministrationDetailBase } from '../../administration-detail-base';
import { GroupTypeModel } from '@app/core/models/groups/group-type.model';
import { MatPaginator } from '@angular/material/paginator';
import { PagedResult } from '@app/core/models/paged-result.model';
import { ModalHelper } from '@app/shared/models/modal-helper.model';
import { MatDialog } from '@angular/material/dialog';
import { ModalComponent } from '@app/shared/components/modal/modal.component';
import { ExcelService } from '@app/core/services/excel.service';
import { ExcelExportFileName } from '@app/core/models/configuration/excel-export-filename.model';
import { ExcelExportColumnsDefinitionConfig } from '@app/core/models/configuration/excel-export-colums-definition-helper';

@Component({
  selector: 'app-user-detail',
  templateUrl: './user-detail.component.html',
  styleUrls: ['./user-detail.component.scss'],
})
export class UserDetailComponent
  extends AdministrationDetailBase
  implements OnInit, OnDestroy
{
  searchInput$ = new EventEmitter<string>();

  userSuggestions: UserModel[] = [];

  groupLines: UserGroupLineModel[] = [];
  userDetail: UserDetailModel;

  groups: GroupModel[] = [];
  pristineUserGroups: UserGroupModel[] = [];
  addedUserGroups: UserGroupModel[] = [];
  modifiedUserGroups: UserGroupModel[] = [];
  deletedUserGroups: UserGroupModel[] = [];
  selectedItemsGroups: UserGroupModel[] = [];

  groupDropDownSettings: IDropdownSettings;

  loading: boolean;
  isDetailMode: boolean;
  selectedUser: boolean;
  hasUnsavedChanges: boolean = false;

  defaultPageSize = 10;
  pageIndex: number = 0;
  pageSize: number = this.defaultPageSize;

  exportColumnsDefinitionConfig = new ExcelExportColumnsDefinitionConfig();
  exportGroupsFileName: string = ExcelExportFileName.USERGROUPS;
  exportGroupsSheetName: string = 'Utilisateurs';

  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;

  constructor(
    groupService: GroupService,
    private route: ActivatedRoute,
    private router: Router,
    public dialog: MatDialog,
    private authInfoService: AuthenticationInfoService,
    private userService: UserService,
    private excelService: ExcelService
  ) {
    super(groupService);
    this.groups = [];
    this.notAssignedGroupOnly = true;
    this.userDetail = new UserDetailModel();
  }

  ngOnInit(): void {
    this.paginator.page
      .pipe(
        tap(() => {
          if (!this.hasUnsavedChanges) {
            this.loadPage();
            return;
          }
          const modalConfig = new ModalHelper().getYesNoConfig(
            `Vous avez effectué une ou des modifications, voulez vous les enregistrer avant de passer à la prochaine page ? Si vous cliquez sur non, la ou les modifications ne seront pas enregistrées`
          );
          this.dialog
            .open(ModalComponent, modalConfig)
            .afterClosed()
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((confirmation: boolean) => {
              if (confirmation) {
                this.onSave(true);
              } else {
                this.loadPage();
              }
            });
        })
      )
      .subscribe();

    this.groupDropDownSettings = new MultiSelectModel().getSettings(
      'Id',
      'Name',
      false,
      true,
      true
    );
    this.groupDropDownSettings.allowRemoteDataSearch = true;

    this.route.params
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((data: Params) => (this.isDetailMode = !_.isNil(data.id)));

    this.route.data
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((resolverData) => {
        if (_.isNil(resolverData.userDetail)) {
          return;
        }
        this.userDetail = resolverData.userDetail;
        this.userId = this.userDetail.Id;
        this.loadUserGroups();
      });

    this.searchInput$
      .pipe(
        debounceTime(200),
        distinctUntilChanged(),
        tap(() => (this.loading = true))
      )
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((value) => {
        if (!_.isNil(value)) {
          this.userService
            .getActiveDirectoryUsers(value)
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((data) => (this.userSuggestions = data));
        }
        this.loading = false;
      });
  }

  protected onGroupFetched(groupType: GroupTypeModel, groups: GroupModel[]) {
    this.groups = groups;
    const deletedGroups = _.filter(this.deletedUserGroups, (g) =>
      g.Name.toLowerCase().includes(this.searchedName.toLowerCase())
    );
    deletedGroups.forEach((userGroup) => {
      this.groups.push(
        new GroupModel(
          userGroup.Id,
          userGroup.Name,
          null,
          null,
          null,
          userGroup.SupervisorId
        )
      );
    });
  }

  onSelectGroup(data: any, action: string): void {
    this.hasUnsavedChanges = true;
    switch (action) {
      case 'select':
        this.synchronizeAddLine(data.Id, data.Name);
        break;
      case 'deselect':
        this.synchronizeRemoveLine(data.Id);
        break;
      case 'selectall':
        this.userDetail.SelectAllGroups = true;
        data.forEach((group) => {
          this.synchronizeAddLine(group.Id, group.Name);
        });
        break;
      case 'deselectall':
        this.userDetail.SelectAllGroups = false;
        this.resetUserGroups();
        break;
    }
  }

  onRemove(userGroup: UserGroupModel): void {
    this.hasUnsavedChanges = true;
    this.synchronizeRemoveLine(userGroup.Id);
  }

  onRemoveSelected(): void {
    this.hasUnsavedChanges = true;
    const selectedLines = this.groupLines.filter((l) => l.Selected);
    selectedLines.forEach((line) => {
      this.synchronizeRemoveLine(line.Id);
    });
  }

  selectAllPages(): void {
    if (!this.userDetail.SelectAllGroups) {
      this.userDetail.DeleteAllOtherPages = false;
    }

    this.userGroupChanged();
  }

  deleteAllOtherPages(): void {
    if (!this.userDetail.DeleteAllOtherPages) {
      this.userDetail.SelectAllGroups = false;
    }
  }

  userGroupChanged(userGroup?: UserGroupModel): void {
    this.hasUnsavedChanges = true;
    if (_.isNil(userGroup)) {
      return;
    }

    const addedGroup = this.addedUserGroups.find(
      (ug) => ug.Id === userGroup.Id
    );
    if (!_.isNil(addedGroup)) {
      this.addedUserGroups = this.addedUserGroups.filter(
        (ug) => ug.Id !== userGroup.Id
      );
      this.addedUserGroups.push(userGroup);
      return;
    }

    const modifiedGroup = this.modifiedUserGroups.find(
      (ug) => ug.Id === userGroup.Id
    );
    if (!_.isNil(modifiedGroup)) {
      this.modifiedUserGroups = this.modifiedUserGroups.filter(
        (ug) => ug.Id !== userGroup.Id
      );
    }
    this.modifiedUserGroups.push(userGroup);
  }

  selectAll(data: any): void {
    this.groupLines.forEach((line) => {
      line.Selected = data.checked;
    });
  }

  get hasAnySelectedLines(): boolean {
    const selectedLines = this.groupLines.filter((s) => s.Selected);
    return selectedLines.length > 0;
  }

  get canDeactivateUser(): boolean {
    return this.userDetail.Groups.filter((g) => g.IsSupervisor).length > 0;
  }

  onChangeSelectSuggestion(selectedUser: UserModel) {
    this.resetAll();
    this.userDetail.Id = selectedUser.Id;
    this.userDetail.IsActive = true;
    this.userDetail.FirstName = selectedUser.FirstName;
    this.userDetail.LastName = selectedUser.LastName;
    this.userDetail.Email = selectedUser.Email;
    this.userDetail.CreationDate = new Date();
  }

  resetUserGroups(): void {
    this.userDetail.Groups = [];
    this.groupLines = [];
    this.selectedItemsGroups = [];
  }

  resetAll(): void {
    this.userDetail = new UserDetailModel();
    this.resetDraftGroups();
    this.resetUserGroups();
  }

  onSave(stayOnPage: boolean = false): void {
    if (!this.isDetailMode) {
      this.userService
        .createUser(this.userDetail)
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe((_) => {
          this.navigateToUserList();
        });
    } else {
      const userUpdate = this.userUpdate;
      this.userService
        .updateUser(userUpdate)
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe((_) => {
          this.authInfoService.refresh();
          if (stayOnPage) {
            this.loadPage();
            return;
          }

          this.navigateToUserList();
        });
    }
  }

  onCancel() {
    this.navigateToUserList();
  }

  onClearCache() {
    this.userService
      .deleteUserProfileCache(this.userDetail.Id)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((_) => {});
  }

  private get userUpdate(): UserUpdateModel {
    const userUpdate = Object.assign(new UserUpdateModel(), this.userDetail);
    userUpdate.DeletedGroups = this.deletedUserGroups.map((ug) => ug.Id);
    userUpdate.ModifiedGroups = this.modifiedUserGroups;
    userUpdate.AddedGroups = this.addedUserGroups;
    return userUpdate;
  }

  private navigateToUserList(): void {
    this.router.navigate(['administration/users']);
  }

  private generateUserGroupLineModel(groupId: number): UserGroupLineModel {
    const group = this.groups.find((g) => g.Id === groupId);
    return new UserGroupLineModel(
      group.Id,
      false,
      _.isNil(group.SupervisorUserId) ||
        group.SupervisorUserId === this.userDetail.Id
    );
  }

  private loadUserGroups(): void {
    this.resetDraftGroups();
    this.userService
      .getUserGroups(
        this.userDetail.Id,
        'Name',
        'asc',
        this.pageIndex,
        this.pageSize
      )
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((data: PagedResult<UserGroupModel>) => {
        this.userDetail.Groups = data.Items;
        this.pristineUserGroups = [...data.Items];
        this.paginator.length = data.TotalItems;

        data.Items.forEach((userGroup) => {
          this.groupLines[userGroup.Id] = new UserGroupLineModel(
            userGroup.Id,
            false,
            userGroup.IsSupervisor || !userGroup.HasSupervisor
          );
        });
      });
  }

  synchronizeRemoveLine(id: number): void {
    this.selectedItemsGroups = this.selectedItemsGroups.filter(
      (g) => g.Id !== id
    );
    this.userDetail.Groups = this.userDetail.Groups.filter((g) => g.Id !== id);
    this.groupLines[id].Selected = false;
    this.deleteGroup(id);
  }

  synchronizeAddLine(id: number, name: string): void {
    _.remove(this.deletedUserGroups, (g) => g.Id === id);
    if (_.isNil(this.userDetail.Groups.find((g) => g.Id == id))) {
      const group = this.groups.find((g) => g.Id == id);
      const userGroup = new UserGroupModel(
        id,
        name,
        new Date(),
        group.SupervisorUserId
      );
      this.userDetail.Groups.push(userGroup);
      this.groupLines[id] = this.generateUserGroupLineModel(id);
      this.addGroup(userGroup);
    }
  }

  private loadPage(): void {
    this.pageIndex = this.paginator.pageIndex;
    this.pageSize = this.paginator.pageSize;
    this.hasUnsavedChanges = false;
    this.loadUserGroups();
  }

  private resetDraftGroups(): void {
    this.deletedUserGroups = [];
    this.addedUserGroups = [];
    this.modifiedUserGroups = [];
    this.pristineUserGroups = [];
  }

  private deleteGroup(groupId: number): void {
    if (
      this.pristineUserGroups.some((ug) => ug.Id === groupId) &&
      !this.deletedUserGroups.some((g) => g.Id === groupId)
    ) {
      this.deletedUserGroups.push(
        this.pristineUserGroups.find((g) => g.Id === groupId)
      );
    }
    if (this.addedUserGroups.find((g) => g.Id === groupId)) {
      this.addedUserGroups = this.addedUserGroups.filter(
        (g) => g.Id !== groupId
      );
    }
    if (this.modifiedUserGroups.find((g) => g.Id === groupId)) {
      this.modifiedUserGroups = this.modifiedUserGroups.filter(
        (g) => g.Id !== groupId
      );
    }
  }

  private addGroup(group: UserGroupModel): void {
    const groupIsNew = _.isNil(
      this.pristineUserGroups.find((ug) => ug.Id === group.Id)
    );
    const groupIsAlreadyAdded = !_.isNil(
      this.addedUserGroups.find((ug) => ug.Id === group.Id)
    );
    if (groupIsNew) {
      if (!groupIsAlreadyAdded) {
        this.addedUserGroups.push(group);
      }
      return;
    }

    const groupIsAlreadyModified = !_.isNil(
      this.modifiedUserGroups.find((ug) => ug.Id === group.Id)
    );
    if (!groupIsAlreadyModified) {
      this.modifiedUserGroups.push(group);
    }
  }

  exportGroupsAsExcel(): any {
    this.userService
      .getAllUserGroups(this.userDetail.Id)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((userGroups: UserGroupModel[]) => {
        this.excelService.exportAsExcel({
          fileName: `${this.exportGroupsFileName}_${this.userDetail.FullName}`,
          data: this.transformData(userGroups),
          sheetName: this.exportGroupsSheetName,
        });
      });
  }

  private transformData(data: UserGroupModel[]): any {
    let numericColumns = [];
    let exportGroupsColumnsDefinitionKeys =
      this.exportColumnsDefinitionConfig.getColumnDefinitionKeys();
    let rowTransformedData = [];

    exportGroupsColumnsDefinitionKeys.map((c) => {
      let columnDefinition =
        this.exportColumnsDefinitionConfig.getColumnDefinition(c).displayName;
      rowTransformedData[columnDefinition] = this.extractData(
        this.userDetail,
        c
      );
    });
    this.groupsColumnsDefinition(data).map((g) => {
      rowTransformedData[g.displayName] = g.groupe;
    });
    return { rows: [rowTransformedData], numericColumns: numericColumns };
  }
  private extractData(data: any, columnDefinition: string): any {
    return data[columnDefinition];
  }

  private groupsColumnsDefinition(groups: UserGroupModel[]): any[] {
    return groups.map((group, index) => {
      return { displayName: `Groupe${index + 1}`, groupe: group.Name };
    });
  }

  ngOnDestroy() {
    this.unsubscribe$.next(true);
  }
}
